/**
 * One step of a workflow, as a panel.
 *
 * The field this exists for is **Run this step on**. It is *one* select over
 * the pairs the grammar admits, and not a model select beside an effort select,
 * because what has to be valid is the **pair**: `contracts/settings/models.ts`
 * stops GPT-5.5 and GPT-5.2 at `xhigh`, and validating the two halves
 * separately admits combinations no session can be launched with. The desktop
 * already picks the pair; the organization's settings page did not, and neither
 * did this one.
 *
 * Everything else is the app's own vocabulary: an instruction is a paragraph
 * held to `MAX_INSTRUCTION_CHARS`, a step runs in a conversation — the
 * session's, a new one, or one it shares by name with other steps — and the gate
 * is read on the way *out* — which is why it is worded as what happens before
 * the next step rather than before this one.
 */
import type React from 'react';
import { MAX_INSTRUCTION_CHARS, MAX_STEP_NAME_CHARS } from '@airlock/contracts/workflow/sanitize';
import { color, font, radius } from '../../../theme/tokens';
import { GhostButton } from '../../../components/primitives';
import { SidePanel } from '../../../components/SidePanel';
import { MODEL_EFFORT_OPTIONS, pairOf, splitPair } from '../org-catalogue';
import type { WorkflowStep } from '../../../lib/types';

const SCOPES: { value: WorkflowStep['check']['scope']; label: string }[] = [
  { value: 'none', label: 'Nothing — the next arrow is always taken' },
  { value: 'changed', label: 'The projects this step changed' },
  { value: 'all', label: 'Every project in the workspace' },
  { value: 'command', label: 'One command, at the workspace root' }
];

/** How the panel reads a step's conversation: the session's, one of its own each visit, or a name. */
function conversationMode(conversation: string): 'main' | 'new' | 'named' {
  return conversation === 'main' || conversation === 'new' ? conversation : 'named';
}

function conversationHint(conversation: string): string {
  if (conversation === 'main') {
    return 'The session’s own conversation for this step’s agent, which still holds what the steps before it read and decided.';
  }
  if (conversation === 'new') {
    return 'A conversation of its own at every visit: it reads what the steps before it reported and the change, not the reasoning behind them — for a step that checks work it should not have watched being written.';
  }
  return 'The steps that share this name come back to the same conversation — a reviewer that remembers what it asked for last time. A conversation belongs to one agent: on another agent, the same name is that agent’s own.';
}

function Label({ children, count }: { children: React.ReactNode; count?: string }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: font.mono,
          fontSize: 10.5,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: color.inkFaint
        }}
      >
        {children}
      </span>
      {count && <span style={{ flex: 'none', fontFamily: font.mono, fontSize: 10, color: color.inkGhost }}>{count}</span>}
    </div>
  );
}

const field: React.CSSProperties = {
  width: '100%',
  padding: '8px 11px',
  border: `1px solid ${color.lineInput}`,
  borderRadius: radius.md,
  background: color.surface,
  fontFamily: 'inherit',
  fontSize: 13,
  lineHeight: 1.45,
  color: color.ink
};

/**
 * The step, in the side panel (§23): its name as the panel's title — no
 * « STEP N » kicker — the fields in the body, whatever the page adds after them
 * (the arrow drawer), and « Remove step » as a secondary button with no modal:
 * nothing leaves before Publish, and Discard brings it back.
 */
export function StepInspector({
  step,
  editable,
  onChange,
  onRemove,
  onClose,
  children
}: {
  step: WorkflowStep;
  editable: boolean;
  onChange: (next: WorkflowStep) => void;
  onRemove: () => void;
  onClose: () => void;
  children?: React.ReactNode;
}): React.JSX.Element {
  const set = (patch: Partial<WorkflowStep>): void => onChange({ ...step, ...patch });
  const setCheck = (patch: Partial<WorkflowStep['check']>): void => set({ check: { ...step.check, ...patch } });

  return (
    <SidePanel id="step-inspector" title={step.name.trim() || 'Untitled step'} closeLabel="Close this step" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Label>Name</Label>
          <input
            value={step.name}
            readOnly={!editable}
            maxLength={MAX_STEP_NAME_CHARS}
            placeholder="Read and write a plan"
            onChange={(event) => set({ name: event.target.value })}
            style={field}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Label count={`${step.instruction.length} / ${MAX_INSTRUCTION_CHARS}`}>Instruction</Label>
          <textarea
            value={step.instruction}
            readOnly={!editable}
            rows={5}
            maxLength={MAX_INSTRUCTION_CHARS}
            placeholder="What the agent is told when a task arrives here."
            onChange={(event) => set({ instruction: event.target.value })}
            style={{ ...field, resize: 'vertical' }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Label>Leave what it finds as notes on the code</Label>
          <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <Switch on={step.annotate} disabled={!editable} onToggle={() => set({ annotate: !step.annotate })} />
            <span style={{ fontSize: 13, color: color.inkBody }}>{step.annotate ? 'Yes' : 'No'}</span>
          </span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: color.inkGhost }}>
            On the lines they are about, in the app's review, for a person to read and send back — nothing reaches the agent
            until they do, and the run waits on them when there are any.
          </span>
        </div>

        {/* Which conversation it runs in (§5.15). Every step is told what the
            steps before it reported, whatever its conversation: this decides only
            whose reasoning it carries on. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Label>Run it in</Label>
          <select
            value={conversationMode(step.conversation)}
            disabled={!editable}
            onChange={(event) => {
              const mode = event.target.value;
              set({ conversation: mode === 'named' ? 'reviewer' : mode });
            }}
            style={field}
          >
            <option value="main">The session’s conversation</option>
            <option value="new">A new one</option>
            <option value="named">A named one</option>
          </select>
          {conversationMode(step.conversation) === 'named' ? (
            <input
              value={step.conversation}
              readOnly={!editable}
              maxLength={24}
              placeholder="reviewer"
              aria-label="Conversation name"
              onChange={(event) => set({ conversation: event.target.value.toLowerCase() })}
              style={{ ...field, fontFamily: font.mono }}
            />
          ) : null}
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: color.inkGhost }}>
            {conversationHint(step.conversation)}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Label>Run this step on</Label>
          <select
            value={pairOf(step.model, step.effort)}
            disabled={!editable}
            onChange={(event) => {
              const { defaultModel, defaultEffort } = splitPair(event.target.value);
              set({ model: defaultModel, effort: defaultEffort ?? '', agent: '' });
            }}
            style={field}
          >
            {MODEL_EFFORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: color.inkGhost }}>
            One field and not two: what has to be valid is the pair, so the pair is what is picked: GPT-5.5 and GPT-5.2 stop
            at X-High. The agent follows from the model, so a chain can hand work from one to the other.
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Label>Then, before the next step</Label>
          <Segmented
            value={step.gate}
            disabled={!editable}
            options={[
              { value: 'auto', label: 'Go ahead' },
              { value: 'ask', label: 'Wait for me' }
            ]}
            onChange={(value) => set({ gate: value as WorkflowStep['gate'] })}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Label>What proves it worked</Label>
          <select
            value={step.check.scope}
            disabled={!editable}
            onChange={(event) => setCheck({ scope: event.target.value as WorkflowStep['check']['scope'] })}
            style={field}
          >
            {SCOPES.map((scope) => (
              <option key={scope.value} value={scope.value}>
                {scope.label}
              </option>
            ))}
          </select>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: color.inkGhost }}>
            The command belongs to each project, because a workspace is several of them and one command cannot be right for
            all. A step says how many of them — whichever the task it runs has. Without a check, only <span style={{ fontFamily: font.mono }}>then</span>{' '}
            can fire — <span style={{ fontFamily: font.mono }}>on pass</span> and{' '}
            <span style={{ fontFamily: font.mono }}>on fail</span> have no outcome to match.
          </span>
        </div>

        {step.check.scope === 'command' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <Label>The command</Label>
            <input
              value={step.check.command}
              readOnly={!editable}
              maxLength={300}
              placeholder="pnpm check"
              onChange={(event) => setCheck({ command: event.target.value })}
              style={{ ...field, fontFamily: font.mono, fontSize: 12.5 }}
            />
          </div>
        )}

        {children}

        {editable && (
          <div style={{ paddingTop: 6 }}>
            <GhostButton size="sm" onClick={onRemove}>
              Remove step
            </GhostButton>
          </div>
        )}
      </div>
    </SidePanel>
  );
}

function Switch({ on, disabled, onToggle }: { on: boolean; disabled?: boolean; onToggle: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onToggle}
      style={{
        position: 'relative',
        flex: 'none',
        width: 32,
        height: 19,
        padding: 0,
        border: `1px solid ${on ? color.ink : color.lineInput}`,
        borderRadius: radius.chip,
        background: on ? color.ink : 'transparent',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'default' : 'pointer'
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 15 : 2,
          width: 13,
          height: 13,
          borderRadius: radius.chip,
          background: on ? '#FFFFFF' : color.inkGhost,
          transition: 'left .12s'
        }}
      />
    </button>
  );
}

function Segmented({
  value,
  options,
  disabled,
  onChange
}: {
  value: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: 2,
        border: `1px solid ${color.line}`,
        borderRadius: radius.md,
        background: color.surfaceAlt
      }}
    >
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            style={{
              flex: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 26,
              border: 0,
              borderRadius: radius.sm,
              background: on ? color.surface : 'transparent',
              boxShadow: on ? '0 1px 2px rgba(22,25,31,.06)' : undefined,
              fontFamily: 'inherit',
              fontSize: 12.5,
              color: on ? color.ink : color.inkSubtle,
              cursor: disabled ? 'default' : 'pointer'
            }}
          >
            {option.label}
          </button>
        );
      })}
    </span>
  );
}
